import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Search, Filter, CircleCheck as CheckCircle2, Clock, Eye, Circle as XCircle, Loader as Loader2, RefreshCw, Send } from 'lucide-react';

interface LogRow {
  id: string;
  unit_id: string;
  contract_id: string | null;
  installment_id: string | null;
  client_name: string | null;
  phone: string;
  template_type: string;
  message_content: string;
  status: string;
  provider_message_id: string | null;
  error_message: string | null;
  scheduled_for: string;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
  batch_id: string | null;
  sender_user_id: string | null;
  sender_scope: string | null;
  instance_id: string | null;
  sender_name?: string;
}

const TEMPLATE_LABELS: Record<string, string> = {
  before_due: 'Antes do vencimento',
  on_due_day: 'Dia do vencimento',
  overdue_1d: 'Atraso 1 dia',
  overdue_3d: 'Atraso 3 dias',
  overdue_7d: 'Atraso 7 dias',
  payment_confirmation: 'Confirmação',
};

const STATUS_STYLES: Record<string, { label: string; icon: any; color: string }> = {
  queued: { label: 'Na fila', icon: Clock, color: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Enviada', icon: Send, color: 'bg-blue-100 text-blue-800' },
  delivered: { label: 'Entregue', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-800' },
  read: { label: 'Lida', icon: Eye, color: 'bg-teal-100 text-teal-800' },
  failed: { label: 'Falha', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

export default function WhatsAppLogs() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [detail, setDetail] = useState<LogRow | null>(null);

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from('whatsapp_message_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(300);
    if (user?.role !== 'admin' && user?.unit_id) {
      query = query.eq('unit_id', user.unit_id);
    }
    const { data } = await query;
    const logs = (data || []) as LogRow[];

    const senderIds = Array.from(new Set(logs.map((l) => l.sender_user_id).filter(Boolean))) as string[];
    if (senderIds.length > 0) {
      const { data: senders } = await supabase.from('users').select('id, name').in('id', senderIds);
      const nameById = new Map((senders || []).map((s: any) => [s.id, s.name]));
      logs.forEach((l) => { l.sender_name = l.sender_user_id ? nameById.get(l.sender_user_id) : undefined; });
    }

    setRows(logs);
    setLoading(false);
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (typeFilter !== 'all' && r.template_type !== typeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (r.client_name || '').toLowerCase().includes(s) ||
          r.phone.includes(s) ||
          r.message_content.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [rows, statusFilter, typeFilter, search]);

  const stats = useMemo(() => {
    const s = { total: rows.length, sent: 0, delivered: 0, read: 0, failed: 0 };
    for (const r of rows) {
      if (r.status === 'failed') s.failed++;
      if (r.status === 'sent' || r.status === 'delivered' || r.status === 'read') s.sent++;
      if (r.status === 'delivered' || r.status === 'read') s.delivered++;
      if (r.status === 'read') s.read++;
    }
    return s;
  }, [rows]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-100 rounded-xl">
            <MessageSquare className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Logs do WhatsApp</h1>
            <p className="text-sm text-slate-600">Histórico completo de mensagens enviadas.</p>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg font-medium transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'bg-slate-50 border-slate-200' },
          { label: 'Enviadas', value: stats.sent, color: 'bg-blue-50 border-blue-200' },
          { label: 'Entregues', value: stats.delivered, color: 'bg-emerald-50 border-emerald-200' },
          { label: 'Lidas', value: stats.read, color: 'bg-teal-50 border-teal-200' },
          { label: 'Falhas', value: stats.failed, color: 'bg-red-50 border-red-200' },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
            <p className="text-xs text-slate-600 uppercase tracking-wide">{s.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar cliente, telefone ou mensagem..."
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">Todos status</option>
              <option value="queued">Na fila</option>
              <option value="sent">Enviada</option>
              <option value="delivered">Entregue</option>
              <option value="read">Lida</option>
              <option value="failed">Falha</option>
            </select>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">Todos tipos</option>
            {Object.entries(TEMPLATE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
            Nenhuma mensagem encontrada.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Telefone</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Enviado por</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Data</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => {
                  const style = STATUS_STYLES[r.status] || STATUS_STYLES.queued;
                  const Icon = style.icon;
                  return (
                    <tr
                      key={r.id}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => setDetail(r)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">{r.client_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{r.phone}</td>
                      <td className="px-4 py-3 text-slate-700">{TEMPLATE_LABELS[r.template_type] || r.template_type}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {r.sender_name ? (
                          <div className="flex flex-col">
                            <span className="text-slate-800">{r.sender_name}</span>
                            <span className="text-xs text-slate-500">
                              {r.sender_scope === 'seller' ? 'WhatsApp pessoal' : r.sender_scope === 'unit' ? 'WhatsApp da unidade' : '—'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.color}`}>
                          <Icon className="w-3 h-3" />
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {new Date(r.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        <Eye className="w-4 h-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Detalhes da mensagem</h3>
              <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <dt className="text-slate-500">Cliente</dt>
              <dd className="col-span-2 text-slate-900">{detail.client_name || '—'}</dd>
              <dt className="text-slate-500">Telefone</dt>
              <dd className="col-span-2 text-slate-900">{detail.phone}</dd>
              <dt className="text-slate-500">Tipo</dt>
              <dd className="col-span-2 text-slate-900">{TEMPLATE_LABELS[detail.template_type] || detail.template_type}</dd>
              <dt className="text-slate-500">Status</dt>
              <dd className="col-span-2 text-slate-900">{STATUS_STYLES[detail.status]?.label || detail.status}</dd>
              <dt className="text-slate-500">Enviado por</dt>
              <dd className="col-span-2 text-slate-900">
                {detail.sender_name || '—'}
                {detail.sender_scope && (
                  <span className="text-xs text-slate-500 ml-2">
                    ({detail.sender_scope === 'seller' ? 'WhatsApp pessoal' : 'WhatsApp da unidade'})
                  </span>
                )}
              </dd>
              <dt className="text-slate-500">Agendada</dt>
              <dd className="col-span-2 text-slate-900">{detail.scheduled_for}</dd>
              <dt className="text-slate-500">Enviada</dt>
              <dd className="col-span-2 text-slate-900">{detail.sent_at ? new Date(detail.sent_at).toLocaleString('pt-BR') : '—'}</dd>
              <dt className="text-slate-500">Entregue</dt>
              <dd className="col-span-2 text-slate-900">{detail.delivered_at ? new Date(detail.delivered_at).toLocaleString('pt-BR') : '—'}</dd>
              <dt className="text-slate-500">Lida</dt>
              <dd className="col-span-2 text-slate-900">{detail.read_at ? new Date(detail.read_at).toLocaleString('pt-BR') : '—'}</dd>
            </dl>
            <div>
              <p className="text-sm text-slate-500 mb-1">Mensagem</p>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 whitespace-pre-wrap">
                {detail.message_content}
              </div>
            </div>
            {detail.error_message && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Erro</p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 whitespace-pre-wrap">
                  {detail.error_message}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
